import { Bound, getCommonBound } from '@blocksuite/global/gfx';
import type { BlockSnapshot } from '@blocksuite/store';

const SURFACE_LAYOUT_OWNED_TYPES = new Set(['connector', 'group']);
const DEFAULT_FREE_PLACEMENT_GAP = 160;
const MAX_FREE_PLACEMENT_SCAN_ROWS = 18;
const MAX_FREE_PLACEMENT_SCAN_COLUMNS = 18;

type SurfaceElementLike = Record<string, unknown>;

type TemplatePlacementBlock = {
  flavour?: unknown;
  props?: Record<string, unknown>;
  children?: readonly TemplatePlacementBlock[];
};

export function getBlockSnapshotContentBound(
  blocks: readonly TemplatePlacementBlock[]
): Bound | null {
  return getCommonBound(collectTemplatePlacementBlockBounds(blocks));
}

export function createRightSidePlacementBound(
  contentBound: Bound,
  gap: number
): Bound {
  return new Bound(
    contentBound.x + contentBound.w + gap,
    contentBound.y,
    contentBound.w,
    contentBound.h
  );
}

export function findFreeBlockSnapshotContentBound({
  blocks,
  width,
  height,
  gap = DEFAULT_FREE_PLACEMENT_GAP,
}: {
  blocks: readonly TemplatePlacementBlock[];
  width: number;
  height: number;
  gap?: number;
}): Bound {
  const placementWidth = isFinitePositive(width) ? width : 100;
  const placementHeight = isFinitePositive(height) ? height : 100;
  const occupied = collectTemplatePlacementBlockBounds(blocks)
    .map(normalizeBound)
    .filter((bound): bound is Bound => !!bound)
    .sort(sortBoundByReadingOrder);

  if (occupied.length === 0) {
    return new Bound(gap, gap, placementWidth, placementHeight);
  }

  const contentBound = getCommonBound(occupied);
  if (!contentBound) {
    return new Bound(gap, gap, placementWidth, placementHeight);
  }
  const maxX = contentBound.x + contentBound.w;
  const maxY = contentBound.y + contentBound.h;
  const candidates: Bound[] = [
    new Bound(
      maxX + gap,
      contentBound.y + contentBound.h / 2 - placementHeight / 2,
      placementWidth,
      placementHeight
    ),
  ];

  for (const bound of occupied) {
    candidates.push(
      new Bound(
        bound.x + bound.w + gap,
        bound.y,
        placementWidth,
        placementHeight
      )
    );
    candidates.push(
      new Bound(
        bound.x,
        bound.y + bound.h + gap,
        placementWidth,
        placementHeight
      )
    );
  }

  const stepX = placementWidth + gap;
  const stepY = placementHeight + gap;
  const gridStartX = maxX + gap;
  const gridStartY = contentBound.y;

  for (let row = 0; row < MAX_FREE_PLACEMENT_SCAN_ROWS; row++) {
    for (let column = 0; column < MAX_FREE_PLACEMENT_SCAN_COLUMNS; column++) {
      candidates.push(
        new Bound(
          gridStartX + column * stepX,
          gridStartY + row * stepY,
          placementWidth,
          placementHeight
        )
      );
    }
  }

  const aboveStartY = contentBound.y - placementHeight - gap;
  for (let row = 0; row < MAX_FREE_PLACEMENT_SCAN_ROWS; row++) {
    for (let column = 0; column < MAX_FREE_PLACEMENT_SCAN_COLUMNS; column++) {
      candidates.push(
        new Bound(
          contentBound.x + column * stepX,
          aboveStartY - row * stepY,
          placementWidth,
          placementHeight
        )
      );
    }
  }

  return (
    uniqueBounds(candidates).find(candidate =>
      isFreeBound(candidate, occupied, gap)
    ) ?? new Bound(maxX + gap, maxY + gap, placementWidth, placementHeight)
  );
}

export function offsetBlockSnapshot(
  block: BlockSnapshot,
  dx: number,
  dy: number,
  parentFlavour: string | undefined = 'affine:page'
): void {
  if (
    isCanvasRenderedBlockParent(parentFlavour) ||
    block.flavour === 'affine:surface'
  ) {
    offsetBlockSnapshotShallow(block, dx, dy);
  }

  block.children.forEach(child =>
    offsetBlockSnapshot(
      child,
      dx,
      dy,
      typeof block.flavour === 'string' ? block.flavour : undefined
    )
  );
}

export function offsetBlockSnapshotShallow(
  block: BlockSnapshot,
  dx: number,
  dy: number
): void {
  block.props.xywh = offsetXywh(block.props.xywh, dx, dy) ?? block.props.xywh;

  if (block.flavour === 'affine:surface') {
    offsetSurfaceElements(block.props.elements, dx, dy);
  }
}

export function offsetBlockSnapshots(
  blocks: readonly BlockSnapshot[],
  dx: number,
  dy: number
): void {
  blocks.forEach(block => offsetBlockSnapshot(block, dx, dy));
}

function collectTemplatePlacementBlockBounds(
  blocks: readonly TemplatePlacementBlock[],
  parentFlavour: string | undefined = 'affine:page'
): Bound[] {
  const bounds: Bound[] = [];

  blocks.forEach(block => {
    if (isCanvasRenderedBlockParent(parentFlavour)) {
      collectXywhBound(block.props?.xywh, bounds);
    }

    if (block.flavour === 'affine:surface') {
      collectSurfaceElementBounds(block.props?.elements, bounds);
    }

    if (Array.isArray(block.children)) {
      bounds.push(
        ...collectTemplatePlacementBlockBounds(
          block.children,
          typeof block.flavour === 'string' ? block.flavour : undefined
        )
      );
    }
  });

  return bounds;
}

function isCanvasRenderedBlockParent(parentFlavour: string | undefined) {
  return parentFlavour === 'affine:page' || parentFlavour === 'affine:surface';
}

function collectSurfaceElementBounds(elements: unknown, bounds: Bound[]): void {
  if (!isRecord(elements)) return;

  Object.values(elements).forEach(element => {
    if (!isRecord(element)) return;
    const type = String(element.type);

    if (!SURFACE_LAYOUT_OWNED_TYPES.has(type)) {
      collectXywhBound(element.xywh, bounds);
    }

    if (type === 'connector') {
      collectConnectorFreeEndpointBounds(element, bounds);
    }
  });
}

function collectConnectorFreeEndpointBounds(
  element: SurfaceElementLike,
  bounds: Bound[]
): void {
  (['source', 'target'] as const).forEach(key => {
    const endpoint = element[key];
    if (!isRecord(endpoint) || endpoint.id) return;
    const position = endpoint.position;
    if (!isPoint(position)) return;
    bounds.push(new Bound(position[0], position[1], 0, 0));
  });
}

function collectXywhBound(value: unknown, bounds: Bound[]): void {
  const bound = readBound(value);
  if (bound) bounds.push(bound);
}

function isFinitePositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function normalizeBound(bound: Bound): Bound | null {
  if (
    !Number.isFinite(bound.x) ||
    !Number.isFinite(bound.y) ||
    !isFinitePositive(bound.w) ||
    !isFinitePositive(bound.h)
  ) {
    return null;
  }
  return bound;
}

function sortBoundByReadingOrder(left: Bound, right: Bound): number {
  return left.y - right.y || left.x - right.x;
}

function intersectsBound(left: Bound, right: Bound, gap: number): boolean {
  return (
    left.x < right.x + right.w + gap &&
    left.x + left.w + gap > right.x &&
    left.y < right.y + right.h + gap &&
    left.y + left.h + gap > right.y
  );
}

function isFreeBound(
  candidate: Bound,
  occupied: readonly Bound[],
  gap: number
): boolean {
  return !occupied.some(bound => intersectsBound(candidate, bound, gap));
}

function uniqueBounds(bounds: Bound[]): Bound[] {
  const seen = new Set<string>();
  return bounds.filter(bound => {
    const key = [bound.x, bound.y, bound.w, bound.h]
      .map(value => Math.round(value))
      .join(':');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function offsetSurfaceElements(
  elements: unknown,
  dx: number,
  dy: number
): void {
  if (!isRecord(elements)) return;

  Object.values(elements).forEach(element => {
    if (!isRecord(element)) return;
    const type = String(element.type);

    if (SURFACE_LAYOUT_OWNED_TYPES.has(type) && element.xywh) {
      delete element.xywh;
    } else {
      element.xywh = offsetXywh(element.xywh, dx, dy) ?? element.xywh;
    }

    if (type === 'connector') {
      offsetConnectorFreeEndpoints(element, dx, dy);
    }
  });
}

function offsetConnectorFreeEndpoints(
  element: SurfaceElementLike,
  dx: number,
  dy: number
): void {
  (['source', 'target'] as const).forEach(key => {
    const endpoint = element[key];
    if (!isRecord(endpoint) || endpoint.id || !isPoint(endpoint.position)) {
      return;
    }
    endpoint.position = [endpoint.position[0] + dx, endpoint.position[1] + dy];
  });
}

function offsetXywh(value: unknown, dx: number, dy: number): string | null {
  const bound = readBound(value);
  if (!bound) return null;
  return new Bound(bound.x + dx, bound.y + dy, bound.w, bound.h).serialize();
}

function readBound(value: unknown): Bound | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  try {
    const bound = Bound.deserialize(value);
    if (
      !Number.isFinite(bound.x) ||
      !Number.isFinite(bound.y) ||
      !Number.isFinite(bound.w) ||
      !Number.isFinite(bound.h)
    ) {
      return null;
    }
    return bound;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPoint(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  );
}
